import {
    useState, useCallback, useMemo, useEffect
} from 'react';
import { useSelector, useDispatch, shallowEqual } from 'react-redux';
import Empty from 'constants/empty';
import DeliveryFrequencyUtils from 'utils/DeliveryFrequency';
import LanguageLocale from 'utils/LanguageLocale';
import OrderUtils from 'utils/Order';
import FormValidator from 'utils/FormValidator';
import DateUtils from 'utils/Date';
import CreditCardUtils from 'utils/CreditCard';
import BasketUtils from 'utils/Basket';
import autoReplenishmentActions from 'actions/AutoReplenishmentActions';
import ShippingAndPaymentInfoSelector from 'selectors/page/autoReplenishment/shippingAndPaymentInfo/shippingAndPaymentInfoSelector';
import AutoReplenishmentBindings from 'analytics/bindingMethods/pages/myAccount/AutoReplenishmentBindings';
import ManageSubscriptionModalEN from 'components/GlobalModals/ManageSubscriptionModal/locales/ManageSubscriptionModal_en_US';
import ManageSubscriptionModalFR from 'components/GlobalModals/ManageSubscriptionModal/locales/ManageSubscriptionModal_fr_CA';

const { formatFrequencyType, formatSavingAmountString, formatCurrency } = DeliveryFrequencyUtils;
const { getLocalisedDataFromResource } = LanguageLocale;
const { shippingAndPaymentInfoSelector } = ShippingAndPaymentInfoSelector;

const PAYMENTS_IMAGE_FOLDER = '/img/ufe/payments';

const getText = getLocalisedDataFromResource(ManageSubscriptionModalEN, ManageSubscriptionModalFR);

/**
 * Transforms payment data into credit card display information
 * @param {Object} payment - Payment information from API
 * @returns {Object} Formatted credit card data or empty object
 */
const transformCreditCardInfo = payment => {
    if (!payment) {
        return Empty.Object;
    }

    const cardType = payment.cardType || Empty.String;
    const cardImage = OrderUtils.getThirdPartyCreditCard({ cardType }) || 'placeholder';
    const label = CreditCardUtils.getCardLabel(cardType);

    return {
        expDate: `${payment.expirationMonth || Empty.String}/${payment.expirationYear || Empty.String}`,
        logo: `${PAYMENTS_IMAGE_FOLDER}/${cardImage}.svg`,
        title: `${cardType} *${payment.cardTokenNumber || Empty.String}`,
        label: `${label} *${payment.cardTokenNumber || Empty.String}`,
        alt: label
    };
};

/**
 * Transforms shipping address data into display information
 * @param {Object} shippingAddress - Shipping address from API
 * @returns {Object} Formatted shipping info or empty object
 */
const transformShippingInfo = shippingAddress => {
    if (!shippingAddress) {
        return Empty.Object;
    }

    const {
        firstName = Empty.String,
        lastName = Empty.String,
        address1 = Empty.String,
        city = Empty.String,
        state = Empty.String,
        zipcode = Empty.String,
        postalCode = Empty.String,
        phoneNumber = Empty.String
    } = shippingAddress;

    return {
        addressLine1: address1,
        phoneNumber: FormValidator.getFormattedPhoneNumber(phoneNumber),
        fullName: `${firstName} ${lastName}`,
        fullAddress: `${city}, ${state} ${zipcode || postalCode}`
    };
};

/**
 * Custom hook for managing subscription modal state and operations
 * @param {Object} params - Hook parameters
 * @param {Object} params.currentSubscription - Current subscription data
 * @param {Function} params.onDismiss - Callback to dismiss the modal
 * @param {Function} params.fireGenericErrorAnalytics - Error analytics callback
 * @returns {Object} Hook return values including creditCard, featureFlags, localization, etc.
 */
export function useManageSubscriptionModal({ currentSubscription, onDismiss, fireGenericErrorAnalytics }) {
    const [isLoading, setIsLoading] = useState(true);

    const dispatch = useDispatch();

    const shippingAndPaymentData = useSelector(shippingAndPaymentInfoSelector, shallowEqual);

    const { frequency, frequencyType, items = Empty.Array } = currentSubscription || Empty.Object;
    const product = items?.[0] || Empty.Object;

    const productData = useMemo(
        () => ({
            ...product,
            productPrice: formatCurrency(parseFloat(BasketUtils.removeCurrency(product.price)), product.qty),
            savingAmount: formatSavingAmountString(product, product.qty, false, true, false, product.acceleratedPromotion)
        }),
        [product]
    );

    const localization = useMemo(() => {
        const frequencyText = `${frequency} ${formatFrequencyType(frequency, frequencyType)}`;

        return {
            askToUpdateDelivery: getText('askToUpdateDelivery'),
            cancelSubscription: getText('cancelSubscription'),
            changeDate: getText('changeDate'),
            creditCardExpDate: getText('creditCardExpDate'),
            deliveryEvery: getText('deliveryEvery'),
            deliveryTitle: getText('deliveryTitle'),
            deliveryValue: getText('deliveryValue', [frequencyText]),
            itemQuantityText: `${getText('qty')} : ${product.qty || 0}`,
            itemText: getText('item', [product.skuId]),
            legalCopy: getText('legalCopy'),
            manageSubscription: getText('manageSubscription'),
            nextShipment: getText('nextShipment'),
            pause: getText('pause'),
            paymentTitle: getText('paymentMethod'),
            qty: getText('qty'),
            shippingTitle: getText('shippingAddress'),
            skip: getText('skip'),
            skuSwapButton: getText('skuSwapButton'),
            skuSwapUnavailable: getText('skuSwapUnavailable'),
            tabHeadingText: `${getText('deliveryEvery')}: ${frequencyText}`
        };
    }, [product, frequency, frequencyType]);

    const featureFlags = useMemo(() => {
        const config = (typeof Sephora !== 'undefined' && Sephora.configurationSettings) || Empty.Object;
        const { autoReplenishmentConfigurations } = config;

        const {
            enableReplenishmentFrequencyModifiable = false,
            enableReplenishmentPaymentModifiable = false,
            enableReplenishmentOperations = false
        } = autoReplenishmentConfigurations || Empty.Object;

        const currentCountry = LanguageLocale.getCurrentCountry();

        // Config structure: { enableReplenishmentAddressModifiable_US: { isEnabled: true } }
        const enableReplenishmentAddressModifiable =
            !!autoReplenishmentConfigurations?.[`enableReplenishmentAddressModifiable_${currentCountry}`]?.isEnabled;

        // Config structure: { skuSwap: { isEnabled: true, isEnabledUS: true, isEnabledCA: false } }
        const skuSwapConfig = autoReplenishmentConfigurations?.skuSwap ?? Empty.Object;
        const changeDateConfig = autoReplenishmentConfigurations?.changeDate ?? Empty.Object;

        return {
            enableReplenishmentAddressModifiable,
            enableReplenishmentDateModifiable: !!(changeDateConfig.isEnabled && changeDateConfig[`isEnabled${currentCountry}`]),
            enableReplenishmentFrequencyModifiable,
            enableReplenishmentOperations,
            enableReplenishmentPaymentModifiable,
            enableReplenishmentSkuSwap: !!(skuSwapConfig.isEnabled && skuSwapConfig[`isEnabled${currentCountry}`])
        };
    }, []);

    const shippingPayment = useMemo(() => {
        const creditCard = transformCreditCardInfo(shippingAndPaymentData?.payment);
        const shippingInfo = transformShippingInfo(shippingAndPaymentData?.shippingAddress);

        return {
            creditCard,
            shippingInfo: {
                ...shippingInfo,
                nextShipmentDate: currentSubscription.nextScheduleRunDate
                    ? DateUtils.getPromiseDate(`${currentSubscription.nextScheduleRunDate}T00:00`)
                    : '-'
            }
        };
    }, [shippingAndPaymentData, currentSubscription]);

    const shouldShowItemVariation = !!(product?.variationType && product?.variationValue && product?.skuId);
    const shouldShowDeliveryMessage = frequency > 1;

    const onModalClose = useCallback(() => {
        AutoReplenishmentBindings.manageSubscriptionClose(currentSubscription);
        onDismiss();
    }, [currentSubscription, onDismiss]);

    /**
     * Load shipping and payment information when the modal opens
     * Set isLoading to true initially and false once data is loaded or fails
     */
    useEffect(() => {
        let isMounted = true;

        const loadData = async () => {
            const { paymentId, profileId, shippingAddressId } = currentSubscription;
            const action = autoReplenishmentActions.loadShippingAndPaymentInfo(
                profileId,
                shippingAddressId,
                paymentId,
                onDismiss,
                fireGenericErrorAnalytics
            );

            try {
                await dispatch(action);
            } finally {
                if (isMounted) {
                    setIsLoading(false);
                }
            }
        };

        loadData();

        return () => {
            isMounted = false;
        };
    }, [currentSubscription, onDismiss, fireGenericErrorAnalytics, dispatch]);

    return {
        ...shippingPayment,
        featureFlags,
        isLoading,
        localization,
        onModalClose,
        product: productData,
        shouldShowDeliveryMessage,
        shouldShowItemVariation
    };
}
